/**
 * Site Context Extraction Discipline — typed-field schema (SCE-1).
 *
 * The Site Context extractor (Gemini Flash) emits `ExtractedClaim` rows
 * with an optional `sedField` dot-path (e.g. `'grid.substationDistKm'`)
 * and a `claimedValue`. Pre-SCE the extractor blindly trusted whatever
 * the LLM returned, so a narrative phrase like *"300 MW BTM data center
 * on a 315-acre site, 67 km to nearest IX point"* would yield
 * `{ sedField: 'grid.substationDistKm', claimedValue: 300 }` — the model
 * grabbed the unlabeled `300` (a power-MW figure) and dropped it into a
 * km-typed distance slot. The resulting diff row read SED `1.141 km` vs
 * Site Context `300` (Diverged), a 263× ratio that's a type-mismatch,
 * not a measurement disagreement.
 *
 * This module exposes a per-SED-path field schema that the extractor
 * (SCE-2) checks every claim against AFTER the LLM call. The schema
 * encodes:
 *
 *   - `expectedType`         — JS type the value must coerce to.
 *   - `requiredUnitTokens`   — for numeric fields, the `sourceSpan` MUST
 *                              contain at least one of these tokens
 *                              (case-insensitive, word-boundary match).
 *                              Without a unit token in the supporting
 *                              prose, the value could be anything;
 *                              refuse rather than guess.
 *   - `plausibleRange`       — [min, max] sanity envelope. Out-of-range
 *                              ⇒ reject (the LLM is hallucinating units
 *                              again).
 *   - `enumValues`           — when the field is a closed enum (state
 *                              codes, USCS soil classes, seismic design
 *                              category). Value must match an entry.
 *   - `crossFieldRules`      — optional pure functions checked at WRITE
 *                              time with access to the live SED / site.
 *                              Each rule returns `{ ok: true }` or
 *                              `{ ok: false, severity, reason }`. A
 *                              `severity: 'block'` rejection drops the
 *                              claim; `'warn'` just stamps a quality
 *                              warning on the claim.
 *
 * Engineering rules (CLAUDE.md §1, §4):
 *   - Pure data + pure functions. No side effects; no IO.
 *   - Unit consistency: unit tokens are stored in the SAME unit family
 *     as the SED field's stored value (e.g. SED stores `substationDistKm`
 *     in km, so the only accepted unit tokens are km/kilometer/kilometers
 *     — `mi`/`miles` is intentionally NOT accepted because there is no
 *     conversion path; users wanting to author miles must convert first
 *     or use the structured authoring form which bypasses this guard).
 *   - Reuses `normaliseStateToCode` + `hasLocusBindingPhrase` from
 *     `inferSiteState.ts` (DAT-FOLLOWUP-1 surface) so SCE's
 *     `regulatory.state` cross-field rule SUPERSEDES the per-doc check;
 *     the locus-binding regex set is the canonical phrase library.
 *
 * Composition with existing surfaces:
 *   - `extractClaims` (SC3, CLP-1) — SCE-2 will fold this schema check
 *     into the extractor's post-validation pass. The extractor already
 *     emits `ExtractorStatus` for runtime classification; SCE rejections
 *     surface as `'rejected'` rows alongside `success` claims.
 *   - `comparators.ts` (SC4) — unchanged. Cross-checker still fires on
 *     accepted claims; SCE just prevents the upstream poisoning that
 *     produced the 300-as-km claim in the first place.
 *   - `sedBindingRegistry.ts` (SA6) — unchanged. The binding surface is
 *     downstream of extraction; if SCE blocks a claim, no binding is
 *     created.
 *
 * Pack reference: docs/prompt-packs/Site_Context_Extraction_Discipline_Prompt.md
 */
import { normaliseStateToCode, hasLocusBindingPhrase } from '../inferSiteState';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { sourceSupportsFiberBuildDistanceFt } from './fiberBuildDistanceEvidence';

/**
 * Result of a single rule evaluation (cross-field, range, unit token,
 * enum, type). Discriminated on `ok` so the consumer can collect all
 * failures for the audit log + telemetry without losing structure.
 */
export type SceRuleResult =
  | { ok: true }
  | { ok: false; severity: 'warn' | 'block'; code: SceRejectionCode; reason: string };

/**
 * Stable telemetry codes — kept narrow so admin dashboards can roll up
 * "5 missing unit token, 4 cross-field mismatch, 2 out-of-range" from
 * the rejected-claim stream. New codes here in lockstep with new rule
 * kinds.
 */
export type SceRejectionCode =
  | 'unknown-field'
  | 'type-mismatch'
  | 'missing-unit-token'
  | 'out-of-range'
  | 'enum-mismatch'
  | 'cross-field-substation-ratio'
  | 'cross-field-state-locus'
  | 'cross-field-distance-ratio'
  // EX-4 — the extracted numeric does not appear near its field keyword
  // in the supplied source text (the value is an IN-RANGE wrong number
  // Gemini grabbed from elsewhere in the doc). Only emitted when the
  // caller supplies a `locusSpan` (the DOC channel does); the
  // site-context channel never sets it, so its codes are unchanged.
  | 'locus-unsupported';

/** Quality-warning ids stamped on lazily-validated legacy claims (SCE-4). */
export const SCE_QUALITY_WARNING_PREFIX = 'sce-' as const;
export type SceQualityWarning =
  | 'sce-type-mismatch'
  | 'sce-out-of-range'
  | 'sce-missing-unit-token'
  | 'sce-enum-mismatch'
  | 'sce-cross-field'
  // SCE-FOLLOWUP-PROBABILISTIC-DISAMBIGUATION (SX-2). Stamped when the
  // extractor returned a value with extractorConfidence < 50 (0-100
  // scale, i.e. the spec's <0.5 confidence band) AND the full input
  // narrative carried multiple plausible candidates of the same type
  // (e.g. two different acre figures, two different distance numerics
  // with the same unit). Does NOT block the claim — the claim still
  // ships into the prompt + cross-checker; the chip is an awareness
  // signal for the user.
  | 'sce-ambiguous';

/**
 * Args every cross-field rule sees. `site` carries the live SiteEntity
 * shape but we only narrow what the rule actually reads (`address`,
 * `location`, `siteContext`) so the type stays import-cheap. `sed`
 * carries the loaded SED when available.
 */
export interface SceCrossFieldContext {
  value: string | number | boolean | undefined;
  sed?: SiteEvidenceDocument | null;
  /**
   * Two-letter USPS code for the site (uppercased). Resolved by the
   * caller via `inferSiteStateFromSed` or the address parser. Cross-field
   * rules that care about state read this instead of digging into the
   * SED themselves so the resolution stays in one place.
   */
  siteStateCode?: string | null;
  /** Optional — site name for locus-binding heuristics. */
  siteName?: string;
}

/**
 * Field schema. Keyed in `FIELD_SCHEMAS` by SED dot-path. Authoring
 * contract:
 *   - `expectedType` is REQUIRED. `'unknown'` is reserved for future
 *     unions we don't have yet — don't use to bypass typing.
 *   - `requiredUnitTokens` is REQUIRED when `expectedType === 'number'`
 *     AND the field carries a physical unit. Pure scalars (counts,
 *     ratios) skip this and just rely on `plausibleRange`.
 *   - `plausibleRange` is RECOMMENDED for every numeric field. The
 *     envelope is conservative — a real value at the edge should still
 *     pass; an extractor hallucination (e.g. capacity-as-distance)
 *     should fail.
 */
export interface FieldSchema {
  /** Dot-path, e.g. `'grid.substationDistKm'`. */
  path: string;
  /** Human label for diff tab / error messages. */
  label: string;
  /** Canonical JS type the value must coerce to. */
  expectedType: 'number' | 'string' | 'boolean';
  /**
   * Required unit tokens (case-insensitive, word-boundary). For numeric
   * fields whose value carries a unit. The `sourceSpan` must contain at
   * least one of these tokens — otherwise the value could be anything.
   * Tokens are stored in the SAME unit family the SED uses; mixed-unit
   * authoring routes through the structured form.
   */
  requiredUnitTokens?: readonly string[];
  /**
   * Plausibility envelope. Inclusive of both bounds. Out-of-range emits
   * `out-of-range` and rejects with `severity: 'block'`. Conservative —
   * a real-world outlier at the edge passes; a 263× hallucination fails.
   */
  plausibleRange?: readonly [number, number];
  /** Enum-typed fields. Value must match (case-sensitive). */
  enumValues?: readonly string[];
  /**
   * EX-4 — field KEYWORDS used by the locus check. When the caller
   * supplies a `locusSpan` (the DOC channel does — it has the doc body),
   * the extracted numeric must appear within `LOCUS_KEYWORD_WINDOW_CHARS`
   * of at least one of these keywords (case-insensitive substring) in
   * that span — UNLESS no keyword appears at all (conservative skip). This
   * is the ONLY check that catches an IN-RANGE wrong value (load-factor
   * 5.5↔83.3, acreage 332↔800). For NUMERIC fields only; absent ⇒ the
   * locus check is skipped for the field entirely.
   */
  locusKeywords?: readonly string[];
  /**
   * Cross-field rules run at WRITE time. Each rule sees the value plus
   * `SceCrossFieldContext`. Block rejections drop the claim; warn
   * rejections stamp `qualityWarnings`.
   */
  crossFieldRules?: readonly ((ctx: SceCrossFieldContext) => SceRuleResult)[];
}

/* ── Unit-token detection ─────────────────────────────────────────── */

/**
 * Word-boundary, case-insensitive scan for any unit token in the source
 * span. Tokens are escaped for regex safety even though current tokens
 * are pure alphanumerics — defensive against future additions.
 *
 * Why word-boundary rather than substring? "ftp" contains "ft" but the
 * substring match would falsely satisfy a feet-typed field. The
 * `\b…\b` regex anchors prevent that without changing the public API.
 *
 * Pure / synchronous. Always returns a boolean.
 */
export function sourceSpanContainsAnyToken(
  sourceSpan: string,
  tokens: readonly string[],
): boolean {
  if (!sourceSpan || tokens.length === 0) return false;
  for (const token of tokens) {
    if (!token) continue;
    const esc = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Some tokens have inner spaces (e.g. "soil class"); for those we
    // still want a leading word-boundary but trail with a lookahead for
    // either a word-boundary or end-of-string.
    const re = new RegExp(`\\b${esc}\\b`, 'i');
    if (re.test(sourceSpan)) return true;
  }
  return false;
}

/* ── EX-4/EX-7: locus-keyword / source-span numeric verification ──────
 *
 * Range / unit / plausibility guards CANNOT catch an IN-RANGE wrong value:
 * a load-factor of 5.5 when the doc says 83.3 (both in [0,100]); a site
 * acreage of 332 when the doc says 800 (both plausible). These are Gemini
 * grabbing the WRONG NUMBER from the document. The only signal that the
 * extracted value is wrong is the SOURCE TEXT itself: the value must
 * appear in / adjacent to its field keyword. The site-context channel
 * already does this per-claim (each claim carries its own `sourceSpan` +
 * `valueAppearsInSpan`); the DOC channel and the intent-prefill channel
 * (EX-7) did not.
 *
 * `extractNumericTokensFromSpan` + `numericAppearsInSpan` are the same
 * tokeniser + FP-tolerant equality the site-context channel uses
 * (extractClaims.ts:extractNumericTokens / valueAppearsInSpan), lifted
 * here so the DOC channel can reuse them without importing the heavy
 * extractor module. They are byte-equivalent in behaviour.
 *
 * `valueSupportedNearKeyword` is the new locus check: it finds each
 * occurrence of any field KEYWORD in the haystack and asks "does the
 * extracted numeric appear, as a token, within ±windowChars of THAT
 * keyword?". Conservative-by-construction so we never over-reject a
 * legitimate value (CLAUDE.md §5):
 *   - No haystack, no keywords, or a non-finite value  ⇒ supported (skip).
 *   - The keyword does not occur anywhere in the text   ⇒ supported
 *     (the doc-extractor passed text Gemini may have paraphrased; if we
 *     can't even find the field's keyword we have no basis to reject).
 *   - The keyword occurs AND the value appears in its window ⇒ supported.
 *   - The keyword occurs but the value appears in NONE of its windows
 *     (the value is somewhere else, bound to a different number near the
 *     keyword) ⇒ NOT supported → the caller rejects / flags.
 */

/**
 * Tokenise standalone integer / decimal numerics out of free text.
 * Mirrors extractClaims.ts:extractNumericTokens — leading word-boundary,
 * trailing non-digit so "10km" yields 10; thousands-separator forms are
 * intentionally not matched. Pure / synchronous.
 */
export function extractNumericTokensFromSpan(text: string): readonly number[] {
  if (!text) return [];
  const out: number[] = [];
  const re = /(?:^|[^0-9.])(-?\d+(?:\.\d+)?)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const n = Number(m[1]);
    if (Number.isFinite(n)) out.push(n);
  }
  return out;
}

/**
 * Does `value` appear as a numeric token in `span`? Small relative
 * tolerance so a float round-trip of the SAME stated figure matches but
 * a different figure (or a geometry-derived float) does not. Mirrors
 * extractClaims.ts:valueAppearsInSpan. Pure / synchronous.
 */
export function numericAppearsInSpan(value: number, span: string): boolean {
  if (!Number.isFinite(value) || !span) return false;
  const tol = Math.max(1e-9, Math.abs(value) * 1e-6);
  for (const t of extractNumericTokensFromSpan(span)) {
    if (Math.abs(t - value) <= tol) return true;
  }
  return false;
}

/** Default proximity window (chars) on each side of a field keyword. A
 *  sentence in this prose runs ~120-200 chars; ±160 keeps the check to
 *  roughly the keyword's own sentence + an immediate neighbour. */
export const LOCUS_KEYWORD_WINDOW_CHARS = 160;

/**
 * EX-4/EX-7 — is the extracted numeric `value` SUPPORTED near one of the
 * field's `keywords` in `haystack`? See the section header for the
 * conservative truth table.
 *
 * Binding rule (mirrors extractClaims.ts:rebindFromSpan keyword-adjacency):
 * within each keyword's ±`windowChars` window, the value is SUPPORTED when
 * it is the numeric token NEAREST that keyword occurrence — i.e. the value
 * the prose actually binds to the field. Merely co-occurring in the window
 * is not enough: in "5.5 MVAR. The load factor is 83.3%", BOTH numbers sit
 * in the window, but only 83.3 is the nearest token to "load factor", so a
 * model that returned 5.5 is rejected and 83.3 is accepted. This is what
 * lets the locus check catch IN-RANGE wrong values that range cannot.
 *
 * Pure / synchronous.
 *
 * @param haystack    Source prose (doc body / excerpt / corpus slice).
 * @param value       The extracted numeric to verify.
 * @param keywords    Field keywords (case-insensitive substring) that
 *                    identify a sentence about THIS field.
 * @param windowChars Half-width of the proximity window. Default 160.
 */
export function valueSupportedNearKeyword(
  haystack: string,
  value: number,
  keywords: readonly string[],
  windowChars: number = LOCUS_KEYWORD_WINDOW_CHARS,
): boolean {
  if (!haystack || !Number.isFinite(value) || keywords.length === 0) return true;
  const lower = haystack.toLowerCase();
  const tol = Math.max(1e-9, Math.abs(value) * 1e-6);
  let keywordSeen = false;
  // Pre-scan every numeric token's [start, end] position once.
  const numRe = /(-?\d+(?:\.\d+)?)/g;
  const numTokens: Array<{ val: number; start: number; end: number }> = [];
  for (let m = numRe.exec(haystack); m !== null; m = numRe.exec(haystack)) {
    const val = Number(m[1]);
    if (Number.isFinite(val)) numTokens.push({ val, start: m.index, end: m.index + m[0].length });
  }
  for (const kw of keywords) {
    const needle = kw.toLowerCase();
    if (!needle) continue;
    for (let idx = lower.indexOf(needle); idx >= 0; idx = lower.indexOf(needle, idx + needle.length)) {
      keywordSeen = true;
      const kwStart = idx;
      const kwEnd = idx + needle.length;
      // Find the in-window numeric token NEAREST this keyword occurrence.
      let nearest: { val: number; dist: number } | undefined;
      for (const t of numTokens) {
        // Distance from the keyword span to the token span (0 if overlap).
        const dist = t.end <= kwStart ? kwStart - t.end : t.start >= kwEnd ? t.start - kwEnd : 0;
        if (dist > windowChars) continue;
        if (!nearest || dist < nearest.dist) nearest = { val: t.val, dist };
      }
      if (nearest && Math.abs(nearest.val - value) <= tol) return true;
    }
  }
  // Keyword(s) never appeared ⇒ we have no basis to reject (conservative).
  // Keyword(s) appeared but the value was never the nearest in-window
  // numeric ⇒ the field binds to a DIFFERENT number → reject.
  return !keywordSeen;
}

/* ── Cross-field rule builders ────────────────────────────────────── */

/**
 * Substation distance ratio guard. Fires when SED already has a value
 * for `grid.substationDistKm` and the new claim disagrees by more than
 * 10×. A genuine measurement disagreement (HIFLD point-to-polygon vs
 * KMZ centroid) sits inside 2×; a 10× ratio is a type mismatch (300 vs
 * 1.141 ⇒ 263×), so we block.
 */
function ruleSubstationDistanceRatio(ctx: SceCrossFieldContext): SceRuleResult {
  if (typeof ctx.value !== 'number' || !Number.isFinite(ctx.value)) return { ok: true };
  const sedRaw = ctx.sed?.grid?.data?.substationDistKm;
  if (typeof sedRaw !== 'number' || !Number.isFinite(sedRaw) || sedRaw <= 0) return { ok: true };
  if (ctx.value <= 0) return { ok: true };
  const ratio = Math.max(ctx.value / sedRaw, sedRaw / ctx.value);
  if (ratio > 10) {
    return {
      ok: false,
      severity: 'block',
      code: 'cross-field-substation-ratio',
      reason:
        `Claimed ${ctx.value} km disagrees with the SED-derived ${sedRaw} km by ${ratio.toFixed(0)}×. ` +
        `That is a type mismatch (likely an unlabeled MW / acres / cost figure), not a measurement disagreement. ` +
        `Refusing extraction — re-state the site context with explicit units.`,
    };
  }
  return { ok: true };
}

/**
 * Generic distance-field ratio guard. Same logic as the substation
 * rule, parameterised by the SED path so we can reuse it for IX
 * distance, reactor distance, etc.
 */
function makeDistanceRatioRule(
  readSed: (sed: SiteEvidenceDocument | null | undefined) => number | undefined,
  label: string,
): (ctx: SceCrossFieldContext) => SceRuleResult {
  return (ctx) => {
    if (typeof ctx.value !== 'number' || !Number.isFinite(ctx.value)) return { ok: true };
    const sedVal = readSed(ctx.sed);
    if (typeof sedVal !== 'number' || !Number.isFinite(sedVal) || sedVal <= 0) return { ok: true };
    if (ctx.value <= 0) return { ok: true };
    const ratio = Math.max(ctx.value / sedVal, sedVal / ctx.value);
    if (ratio > 10) {
      return {
        ok: false,
        severity: 'block',
        code: 'cross-field-distance-ratio',
        reason:
          `Claimed ${ctx.value} km for ${label} disagrees with the SED value ${sedVal} km by ${ratio.toFixed(0)}×. ` +
          `Refusing extraction — likely an unlabeled non-distance figure in the narrative.`,
      };
    }
    return { ok: true };
  };
}

/**
 * State-locus guard. The site has a known state (resolved by the
 * caller into `siteStateCode`); a `regulatory.state` claim that
 * disagrees with the site state is only legitimate if the source span
 * carries a locus-binding phrase ("the site is located in <state>",
 * "<site name> in <state>", etc.).
 *
 * This SUPERSEDES DAT-FOLLOWUP-1's per-doc check for the site-context
 * channel — the doc extractor's check still runs for SED rebuilds, but
 * SCE prevents the site-context narrative from poisoning the SED in
 * the first place.
 */
function ruleStateLocus(
  ctx: SceCrossFieldContext,
  sourceSpan: string | undefined,
): SceRuleResult {
  if (typeof ctx.value !== 'string') return { ok: true };
  const claimedCode = normaliseStateToCode(ctx.value);
  if (!claimedCode) {
    return {
      ok: false,
      severity: 'block',
      code: 'enum-mismatch',
      reason: `Value "${ctx.value}" does not normalise to a US state code.`,
    };
  }
  const siteCode = ctx.siteStateCode ? ctx.siteStateCode.toUpperCase() : null;
  if (!siteCode) return { ok: true }; // no site prior — accept
  if (claimedCode === siteCode) return { ok: true };
  // Cross-state — require locus-binding phrase in the source span.
  if (sourceSpan && hasLocusBindingPhrase(sourceSpan, claimedCode, { siteName: ctx.siteName })) {
    return { ok: true };
  }
  return {
    ok: false,
    severity: 'block',
    code: 'cross-field-state-locus',
    reason:
      `Site is in ${siteCode} but extracted regulatory.state="${claimedCode}". ` +
      `Refusing — the source span does not carry a locus-binding phrase. ` +
      `Use the Author Evidence form if this regulatory body is genuinely cross-state.`,
  };
}

/* ── Registry ─────────────────────────────────────────────────────── */

/**
 * The static registry. Keyed by SED dot-path. New entries go here in
 * lockstep with a test in `__tests__/fieldSchema.test.ts`.
 *
 * Coverage strategy: start with the fields the user can demonstrably
 * mis-extract today (the bug report mentions substationDistKm + state;
 * DAT history adds parcel.acres, capacity, reactor distance, IX
 * distance, seismic class). Future SCE waves can extend coverage; the
 * extractor's `ALLOWED_SED_FIELDS` set in `extractClaims.ts` is the
 * upper bound on what we ever need to schema-validate here.
 */
export const FIELD_SCHEMAS: Readonly<Record<string, FieldSchema>> = Object.freeze({
  // ── grid ─────────────────────────────────────────────────────────
  'grid.substationDistKm': {
    path: 'grid.substationDistKm',
    label: 'Distance to substation (km)',
    expectedType: 'number',
    requiredUnitTokens: ['km', 'kilometer', 'kilometers', 'kilometre', 'kilometres'],
    plausibleRange: [0, 100],
    crossFieldRules: [ruleSubstationDistanceRatio],
  },
  'grid.capacity': {
    path: 'grid.capacity',
    label: 'Grid capacity',
    // The SED field is a free-form string (e.g. "230 kV / 1200 MVA").
    // We only assert it's a string — no unit guard because the value
    // itself may carry multiple units inline.
    expectedType: 'string',
  },
  // ── parcel ────────────────────────────────────────────────────────
  'parcel.acres': {
    path: 'parcel.acres',
    label: 'Parcel area (acres)',
    expectedType: 'number',
    requiredUnitTokens: ['acre', 'acres', 'ac'],
    // 0.1 acre is a tiny lot; 100,000 acres is the Pantex / WIPP range
    // — generous because the user can legitimately quote a much larger
    // parent parcel that's later subdivided.
    plausibleRange: [0.1, 100_000],
    // EX-4 — the acreage figure must appear near a parcel/site/land
    // keyword in the doc body. Catches the 332↔800 first-match grab where
    // BOTH figures are plausible: 332 came from a stray "332-acre county
    // tract" mention while the SITE is "800 acres".
    locusKeywords: ['acre', 'acres', 'parcel', 'site', 'land', 'lot', 'tract', 'property'],
  },
  // ── regulatory ────────────────────────────────────────────────────
  'regulatory.state': {
    path: 'regulatory.state',
    label: 'State of jurisdiction',
    expectedType: 'string',
    // State codes drive a closed enum; the rule below also accepts full
    // names (via normaliseStateToCode) so the enumValues list here is
    // for telemetry / docs rather than direct equality. The locus rule
    // is the load-bearing check.
    enumValues: [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
      'DC',
    ],
    // crossFieldRules attaches the source-span at validation time
    // because the rule needs both ctx + the span. See validateClaim().
  },
  // ── connectivity ──────────────────────────────────────────────────
  'connectivity.nearestIxDistKm': {
    path: 'connectivity.nearestIxDistKm',
    label: 'Distance to nearest internet exchange (km)',
    expectedType: 'number',
    requiredUnitTokens: ['km', 'kilometer', 'kilometers', 'kilometre', 'kilometres'],
    plausibleRange: [0, 2000],
    crossFieldRules: [
      makeDistanceRatioRule(
        (sed) => {
          const d = (sed as { connectivity?: { data?: { nearestIxDistKm?: number } } } | null | undefined)
            ?.connectivity?.data?.nearestIxDistKm;
          return typeof d === 'number' ? d : undefined;
        },
        'connectivity.nearestIxDistKm',
      ),
    ],
  },
  // ── nuclear infra ─────────────────────────────────────────────────
  'nuclearInfra.nearestReactorDistKm': {
    path: 'nuclearInfra.nearestReactorDistKm',
    label: 'Distance to nearest reactor (km)',
    expectedType: 'number',
    requiredUnitTokens: ['km', 'kilometer', 'kilometers', 'kilometre', 'kilometres'],
    plausibleRange: [0, 5000],
    crossFieldRules: [
      makeDistanceRatioRule(
        (sed) => {
          const d = (sed as { nuclearInfra?: { data?: { nearestReactorDistKm?: number } } } | null | undefined)
            ?.nuclearInfra?.data?.nearestReactorDistKm;
          return typeof d === 'number' ? d : undefined;
        },
        'nuclearInfra.nearestReactorDistKm',
      ),
    ],
  },
  // ── seismic ───────────────────────────────────────────────────────
  'seismic.designCategory': {
    path: 'seismic.designCategory',
    label: 'Seismic design category',
    expectedType: 'string',
    enumValues: ['A', 'B', 'C', 'D', 'E', 'F'],
  },
  // ── flood ─────────────────────────────────────────────────────────
  'flood.floodZone': {
    path: 'flood.floodZone',
    label: 'FEMA flood zone',
    expectedType: 'string',
    // FEMA zones: A, AE, AH, AO, AR, A99, V, VE, X, D, M, P (plus
    // sub-codes). We accept a string and let the comparator handle
    // sub-code variants; the schema's job is rejecting numeric
    // hallucinations into this slot.
  },
  'flood.sfha': {
    path: 'flood.sfha',
    label: 'Special Flood Hazard Area (boolean)',
    expectedType: 'boolean',
  },
  // ── environmental ─────────────────────────────────────────────────
  'environmental.hasWetlands': {
    path: 'environmental.hasWetlands',
    label: 'Wetlands present',
    expectedType: 'boolean',
  },
  'environmental.wetlandAcres': {
    path: 'environmental.wetlandAcres',
    label: 'Wetland acres',
    expectedType: 'number',
    requiredUnitTokens: ['acre', 'acres', 'ac'],
    plausibleRange: [0, 100_000],
    // EX-4 — the wetland figure must sit near a wetland keyword, not be a
    // parcel/buffer acreage grabbed from elsewhere in the survey.
    locusKeywords: ['wetland', 'wetlands'],
  },
  // ── water ─────────────────────────────────────────────────────────
  'waterStress.stressLevel': {
    path: 'waterStress.stressLevel',
    label: 'Water stress level',
    expectedType: 'string',
    enumValues: ['Low', 'Low-Medium', 'Moderate', 'Medium', 'Medium-High', 'High', 'Extremely High', 'Unknown'],
  },
  // ── climate ───────────────────────────────────────────────────────
  'climate.peakWetBulb': {
    path: 'climate.peakWetBulb',
    label: 'Peak wet-bulb temperature (°C)',
    expectedType: 'number',
    requiredUnitTokens: ['c', 'celsius', 'wet-bulb', 'wet bulb', 'wetbulb'],
    plausibleRange: [-10, 40],
  },
  // ── EX-1/EX-2 DOC-CHANNEL fields ───────────────────────────────────
  // The fields below are consumed by the DOC channel
  // (`extractFromDocument`) via `validateValue`. They carry NO
  // `requiredUnitTokens`: Gemini structured-output writes the value to a
  // typed JSON slot rather than citing a unit in prose, so a unit-token
  // requirement would over-reject every legitimate doc extraction.
  // Only the type + range guard runs for them. Ranges are physically
  // conservative envelopes (CLAUDE.md §4: a real value sits comfortably
  // inside; a unit/scale hallucination — °F-as-°C, %-in-a-temp-slot,
  // capacity-as-distance — falls out).
  'climate.avgTemp': {
    path: 'climate.avgTemp',
    label: 'Annual average temperature (°C)',
    expectedType: 'number',
    plausibleRange: [-60, 60],
  },
  'climate.peakTemp': {
    path: 'climate.peakTemp',
    label: 'Peak temperature (°C)',
    expectedType: 'number',
    plausibleRange: [-50, 70],
  },
  'climate.avgWetBulb': {
    path: 'climate.avgWetBulb',
    label: 'Annual average wet-bulb temperature (°C)',
    expectedType: 'number',
    plausibleRange: [-40, 40],
  },
  'climate.humidity': {
    path: 'climate.humidity',
    label: 'Annual average relative humidity (%)',
    expectedType: 'number',
    plausibleRange: [0, 100],
    // EX-4 — the % figure must sit near a humidity keyword. Catches an
    // in-range wrong value (e.g. a 5.5 wind-speed figure grabbed into the
    // humidity slot when the doc's humidity is 62%).
    locusKeywords: ['humidity', 'relative humidity', 'rh'],
  },
  // Capacity / load factor as a PERCENTAGE (0-100). Requirement-doc /
  // financial fields carry the factor as a percent rather than a
  // fraction. The locus keywords are the load-bearing guard here (both
  // 5.5 and 83.3 are in-range), so range alone never catches the
  // load-factor 5.5↔83.3 swap — only the locus check binds the value to
  // the figure adjacent to the load/capacity-factor keyword.
  //
  // NOTE: the doc schema's `wind.capacityFactor` slot is a FRACTION (0-1)
  // and is deliberately NOT given a locus check — docs report "38%" while
  // the slot stores 0.38, so a token-match locus check would false-reject
  // a correct unit conversion; its [0,1] range guard already rejects the
  // percentage-scale 5.5 / 83.3 values.
  'capacityFactorPct': {
    path: 'capacityFactorPct',
    label: 'Capacity / load factor (%)',
    expectedType: 'number',
    plausibleRange: [0, 100],
    locusKeywords: ['capacity factor', 'load factor', 'capacity-factor', 'load-factor', 'utilization', 'utilisation'],
  },
  // PGA in g. The strongest recorded ground motions are ~3 g; a value
  // above 5 g is a unit error (gal / cm·s⁻² mistaken for g).
  'seismic.pga': {
    path: 'seismic.pga',
    label: 'Peak ground acceleration (g)',
    expectedType: 'number',
    plausibleRange: [0, 5],
  },
  // Nearest fiber corridor distance (km). KSI authoring stores km. Even
  // a very remote site sits well under ~100 km from a fiber corridor; a
  // larger figure is a wrong-field grab (the live-prod 204.7 "fiber"
  // non-distance — actually a count/latency the model dropped into the
  // distance slot). 100 km is already an extreme buildable-site outlier,
  // so the bound stays conservative while catching the gross error.
  'connectivity.nearestFiberKm': {
    path: 'connectivity.nearestFiberKm',
    label: 'Distance to nearest fiber corridor (km)',
    expectedType: 'number',
    plausibleRange: [0, 100],
    locusKeywords: ['fiber', 'fibre', 'fiber corridor', 'fibre corridor'],
  },
  'connectivity.fiberBuildDistanceFt': {
    path: 'connectivity.fiberBuildDistanceFt',
    label: 'Fiber build distance to handoff (ft)',
    expectedType: 'number',
    requiredUnitTokens: ['ft', 'feet', 'foot'],
    // Broad construction-length envelope; source role, not proximity, binds the value.
    plausibleRange: [0, 1_000_000],
  },
  // Retail rate in ¢/kWh. US retail ranges ~5-45 ¢/kWh; [0, 200]
  // tolerates outlier jurisdictions / demand charges while rejecting a
  // $/kWh figure (~0.10) or a raw MWh figure dropped into the slot.
  'electricity.retailRate': {
    path: 'electricity.retailRate',
    label: 'Retail electricity rate (¢/kWh)',
    expectedType: 'number',
    plausibleRange: [0, 200],
  },
  // AQI is defined on [0, 500]; above is out of the index's domain
  // (a raw pollutant concentration written into the AQI slot).
  'airQuality.aqi': {
    path: 'airQuality.aqi',
    label: 'Air Quality Index',
    expectedType: 'number',
    plausibleRange: [0, 500],
  },
  // GHI in kWh/m²/day. Surface daily GHI tops out near 9-10; above ~15
  // is a unit error (annual kWh/m² as daily, or instantaneous W/m²).
  'solar.ghi': {
    path: 'solar.ghi',
    label: 'Global horizontal irradiance (kWh/m²/day)',
    expectedType: 'number',
    plausibleRange: [0, 15],
  },
  // ── QP-3 (quality pass) — ranges for the previously-unguarded numeric
  // extraction keys. Same philosophy as EX-2/EX-3: BLOCK bounds are
  // deliberately WIDE — they catch unit confusion, sign errors and
  // hallucinated magnitudes, never a domain outlier. Basis noted per key.
  'waterStress.bwsRaw': {
    path: 'waterStress.bwsRaw',
    label: 'Baseline water stress (raw)',
    expectedType: 'number',
    // WRI Aqueduct BWS raw score is defined on [0, 5].
    plausibleRange: [0, 5],
  },
  'seismic.sds': {
    path: 'seismic.sds',
    label: 'Design spectral acceleration SDS (g)',
    expectedType: 'number',
    // ASCE 7 SDS: extreme mapped values stay <3g; 5g is a gross-error bound.
    plausibleRange: [0, 5],
  },
  'seismic.sd1': {
    path: 'seismic.sd1',
    label: 'Design spectral acceleration SD1 (g)',
    expectedType: 'number',
    // SD1 runs lower than SDS everywhere; 3g is a gross-error bound.
    plausibleRange: [0, 3],
  },
  'electricity.year': {
    path: 'electricity.year',
    label: 'Tariff data year',
    expectedType: 'number',
    plausibleRange: [1990, 2100],
  },
  'airQuality.pm25Conc': {
    path: 'airQuality.pm25Conc',
    label: 'PM2.5 concentration (µg/m³)',
    expectedType: 'number',
    // Annual means top out well under 150; 500 covers hazardous episodic
    // peaks a doc might quote while still catching mg/m³ unit confusion.
    plausibleRange: [0, 500],
  },
  'nuclearInfra.nearestReactorPower': {
    path: 'nuclearInfra.nearestReactorPower',
    label: 'Nearest reactor power (MWe)',
    expectedType: 'number',
    // Largest single units ~1,650 MWe; 10,000 tolerates docs quoting a
    // multi-unit PLANT total while rejecting kW/W magnitude errors.
    plausibleRange: [0, 10_000],
  },
  'nuclearInfra.reactorsWithin200km': {
    path: 'nuclearInfra.reactorsWithin200km',
    label: 'Reactors within 200 km',
    expectedType: 'number',
    plausibleRange: [0, 100],
  },
  'solar.dni': {
    path: 'solar.dni',
    label: 'Direct normal irradiance (kWh/m²/day)',
    expectedType: 'number',
    // Peak site annual DNI ~10-11 (Atacama); 15 mirrors the GHI bound.
    plausibleRange: [0, 15],
  },
  'wind.windSpeed100m': {
    path: 'wind.windSpeed100m',
    label: 'Mean wind speed at 100 m (m/s)',
    expectedType: 'number',
    // Site annual means top out ~12 m/s; 30 rejects km/h or mph confusion
    // for any realistic mean while never blocking a windy ridge.
    plausibleRange: [0, 30],
  },
  'wind.windSpeed80m': {
    path: 'wind.windSpeed80m',
    label: 'Mean wind speed at 80 m (m/s)',
    expectedType: 'number',
    plausibleRange: [0, 30],
  },
  'wind.capacityFactor': {
    path: 'wind.capacityFactor',
    label: 'Wind capacity factor (fraction)',
    expectedType: 'number',
    // Stored as a FRACTION (0-1) by the doc schema; a doc-quoted "38"
    // (percent) must be rejected here, not silently stored as 3800%.
    plausibleRange: [0, 1],
  },
  'connectivity.ixCount50km': {
    path: 'connectivity.ixCount50km',
    label: 'Internet exchanges within 50 km',
    expectedType: 'number',
    plausibleRange: [0, 500],
  },
  'connectivity.facilityCount50km': {
    path: 'connectivity.facilityCount50km',
    label: 'Colocation facilities within 50 km',
    expectedType: 'number',
    // Densest metros (N. Virginia) run ~300+; 2000 is a gross-error bound.
    plausibleRange: [0, 2000],
  },
  // ── EX-3 REQUIREMENT-DOC numeric fields ────────────────────────────
  // Keyed by the requirement authored-schema field NAME (not a SED
  // dot-path) because the requirement-doc extractor emits flat authored
  // keys. Ranges are deliberately WIDE — they catch gross magnitude /
  // sign errors only, never a domain-expert outlier.
  'capacityOffered_MW': {
    path: 'capacityOffered_MW',
    label: 'Utility capacity offered (MW)',
    expectedType: 'number',
    plausibleRange: [0, 10_000],
  },
  'costEstimate_USD': {
    path: 'costEstimate_USD',
    label: 'Interconnection cost estimate (USD)',
    expectedType: 'number',
    plausibleRange: [0, 1e12],
  },
  'bfe_ft': {
    path: 'bfe_ft',
    label: 'Base flood elevation (ft)',
    expectedType: 'number',
    plausibleRange: [-50, 30_000],
  },
  'topOfLowestFloor_ft': {
    path: 'topOfLowestFloor_ft',
    label: 'Top of lowest floor elevation (ft)',
    expectedType: 'number',
    plausibleRange: [-50, 30_000],
  },
  'bearingCapacity_kPa': {
    path: 'bearingCapacity_kPa',
    label: 'Allowable bearing capacity (kPa)',
    expectedType: 'number',
    plausibleRange: [0, 100_000],
  },
  'boreholeDepth_m': {
    path: 'boreholeDepth_m',
    label: 'Borehole depth (m)',
    expectedType: 'number',
    plausibleRange: [0, 1_000],
  },
  'groundwaterDepth_m': {
    path: 'groundwaterDepth_m',
    label: 'Groundwater depth (m)',
    expectedType: 'number',
    plausibleRange: [0, 1_000],
  },
});

/* ── Validation API ───────────────────────────────────────────────── */

export interface SceValidateClaimInput {
  /** SED dot-path the claim targets. */
  sedField: string | undefined;
  /** Raw extractor `claimedValue` (already type-coerced by extractClaims). */
  value: string | number | boolean | undefined;
  /** Verbatim slice of the narrative the LLM cited. */
  sourceSpan: string | undefined;
  /** Live SED — needed for distance-ratio cross-field rules. Pass null when not available. */
  sed?: SiteEvidenceDocument | null;
  /** Two-letter USPS code for the site (locus rule). */
  siteStateCode?: string | null;
  /** Site name (locus-binding heuristic widens with siteName). */
  siteName?: string;
}

export interface SceValidateClaimResult {
  /** True when EVERY rule with `severity: 'block'` passed. */
  ok: boolean;
  /** Every blocked / warned rule that fired. Empty when ok and clean. */
  failures: readonly Extract<SceRuleResult, { ok: false }>[];
  /** Quality warnings to stamp on the claim when ok is true but warnings fired. */
  qualityWarnings: readonly SceQualityWarning[];
}

/* ── EX-1: shared, context-free validation primitive ──────────────────
 *
 * `validateValue` is the ONE source-of-truth range / unit / plausibility
 * / enum / type check keyed by canonical SED field path. It runs ONLY the
 * context-free rules (type, range, enum, unit token) against
 * `FIELD_SCHEMAS[fieldPath]`. It does NOT run cross-field / state-locus
 * rules — those need a live SED + are SED-claim-specific, so they stay in
 * `validateClaim` which layers them on top of this primitive.
 *
 * Both the SED-path layer (`validateClaim`, site-context channel) and the
 * DOC channel (`extractFromDocument` → `validateSectionData`) consume this
 * single primitive, so a wrong-unit / out-of-range / implausible-magnitude
 * value is caught identically wherever it enters. The category-keyed
 * `SCV_CATEGORY_RULES` (extractClaims.ts) is a DELIBERATELY complementary
 * sibling: it covers category-level claims with no `sedField`, which this
 * SED-path-keyed primitive cannot key on.
 *
 * Conservative-by-construction: an unknown `fieldPath`, or a
 * `null` / `undefined` value, returns `{ ok: true, failures: [] }` — we
 * never over-reject. This mirrors `validateClaim`'s early-returns so the
 * doc channel can call it on every key without risking blanking a
 * legitimate slot (CLAUDE.md §5).
 */
export interface ValidateValueOptions {
  /**
   * Supporting prose the value was cited from. When a schema declares
   * `requiredUnitTokens`, the span (or `unit`) MUST contain one of them.
   * Pass `undefined` to skip the unit-token check entirely (the DOC
   * channel does this — Gemini writes the value into a typed slot, not
   * into citable prose, so a unit-token requirement would over-reject).
   */
  sourceSpan?: string;
  /**
   * An explicit unit string (e.g. `'km'`). Searched for a required unit
   * token alongside `sourceSpan`. Either satisfies the unit guard.
   */
  unit?: string;
  /**
   * EX-4 — source PROSE the value should be verifiable against by the
   * locus check (field keyword ↔ numeric proximity). The DOC channel
   * passes the doc body / excerpt here; the site-context channel does NOT
   * (its per-claim `sourceSpan` is already the locus, validated upstream),
   * so its behaviour is unchanged. When `locusSpan` is absent OR the
   * schema declares no `locusKeywords`, the locus check is SKIPPED
   * entirely (conservative — no source to verify against ⇒ never reject).
   */
  locusSpan?: string;
  /** EX-4 — override the proximity half-window (chars). Default
   *  `LOCUS_KEYWORD_WINDOW_CHARS`. */
  locusWindowChars?: number;
}

export interface ValidateValueResult {
  /** True when no `severity: 'block'` failure fired. */
  ok: boolean;
  /** Every blocked failure that fired (empty when ok). */
  failures: readonly Extract<SceRuleResult, { ok: false }>[];
}

/**
 * Context-free validation of a single value against the canonical
 * registry. Pure / synchronous.
 *
 * Checks (in order, all collected — not first-fail):
 *   1. Type — value must coerce to `schema.expectedType`.
 *   2. Range — numeric value must sit inside `schema.plausibleRange`.
 *   3. Enum — string value must be in `schema.enumValues` (except
 *      `regulatory.state`, whose locus rule lives in `validateClaim`).
 *   4. Unit token — when `schema.requiredUnitTokens` is set AND the
 *      caller supplied a `sourceSpan` or `unit`, one token must appear.
 *      When NEITHER is supplied, the unit check is SKIPPED (conservative:
 *      the DOC channel has no prose to cite, so requiring a token there
 *      would blank every legitimate value).
 *
 * Returns `{ ok: true, failures: [] }` for an unknown `fieldPath` or a
 * `null` / `undefined` value.
 */
export function validateValue(
  fieldPath: string,
  value: string | number | boolean | undefined | null,
  opts?: ValidateValueOptions,
): ValidateValueResult {
  const failures: Array<Extract<SceRuleResult, { ok: false }>> = [];
  if (!fieldPath) return { ok: true, failures };
  const schema = FIELD_SCHEMAS[fieldPath];
  if (!schema) return { ok: true, failures };
  if (value === undefined || value === null) return { ok: true, failures };

  // 1. Type.
  if (typeof value !== schema.expectedType) {
    failures.push({
      ok: false,
      severity: 'block',
      code: 'type-mismatch',
      reason: `Expected ${schema.expectedType} for ${schema.path}, got ${typeof value}.`,
    });
  }

  // 2. Plausibility range.
  if (
    typeof value === 'number' &&
    schema.plausibleRange &&
    (value < schema.plausibleRange[0] || value > schema.plausibleRange[1])
  ) {
    failures.push({
      ok: false,
      severity: 'block',
      code: 'out-of-range',
      reason:
        `Value ${value} is outside the plausible range [${schema.plausibleRange[0]}, ${schema.plausibleRange[1]}] ` +
        `for ${schema.label}.`,
    });
  }

  // 3. Enum (state codes accept the full-name path via normaliseStateToCode,
  //    handled by validateClaim's cross-field locus rule instead).
  if (
    typeof value === 'string' &&
    schema.enumValues &&
    schema.path !== 'regulatory.state' &&
    !schema.enumValues.includes(value)
  ) {
    failures.push({
      ok: false,
      severity: 'block',
      code: 'enum-mismatch',
      reason: `Value "${value}" is not in the enum set for ${schema.label}.`,
    });
  }

  // 4. Unit token on numeric fields with a unit family — ONLY when the
  //    caller gave us prose / a unit to search. No span + no unit ⇒ skip
  //    (the DOC channel path) so we never over-reject a typed value.
  if (
    typeof value === 'number' &&
    schema.requiredUnitTokens &&
    schema.requiredUnitTokens.length > 0
  ) {
    const span = opts?.sourceSpan;
    const unit = opts?.unit;
    if (span !== undefined || unit !== undefined) {
      const haystack = `${span ?? ''} ${unit ?? ''}`;
      if (!sourceSpanContainsAnyToken(haystack, schema.requiredUnitTokens)) {
        failures.push({
          ok: false,
          severity: 'block',
          code: 'missing-unit-token',
          reason:
            `Source span for ${schema.label} does not contain a unit token from ` +
            `[${schema.requiredUnitTokens.join(', ')}]. Span: "${truncate(haystack.trim(), 120)}".`,
        });
      }
    }
  }

  // 5. Locus-keyword / source-span verification (EX-4). The ONLY check
  //    that catches an IN-RANGE wrong value (load-factor 5.5↔83.3,
  //    acreage 332↔800) — type/range/enum/unit all pass for these. Runs
  //    ONLY when the caller supplied a `locusSpan` AND the schema
  //    declares `locusKeywords` AND the value is numeric. Conservative:
  //    `valueSupportedNearKeyword` returns true (skip) when the keyword
  //    does not appear in the span at all, so a paraphrased / keyword-less
  //    excerpt never blanks a legitimate value.
  if (
    typeof value === 'number' &&
    schema.locusKeywords &&
    schema.locusKeywords.length > 0 &&
    opts?.locusSpan !== undefined
  ) {
    const supported = valueSupportedNearKeyword(
      opts.locusSpan,
      value,
      schema.locusKeywords,
      opts.locusWindowChars,
    );
    if (!supported) {
      failures.push({
        ok: false,
        severity: 'block',
        code: 'locus-unsupported',
        reason:
          `Value ${value} for ${schema.label} does not appear near any of its keywords ` +
          `[${schema.locusKeywords.join(', ')}] in the source text — it is likely a wrong ` +
          `number grabbed from elsewhere in the document. Refusing extraction.`,
      });
    }
  }

  // A documented build quantity is not a nearest-corridor or full-route
  // quantity. Without source prose, retain the existing typed/manual path.
  const fiberBuildSource = opts?.locusSpan ?? opts?.sourceSpan;
  if (fieldPath === 'connectivity.fiberBuildDistanceFt' && typeof value === 'number'
    && fiberBuildSource !== undefined && !sourceSupportsFiberBuildDistanceFt(fiberBuildSource, value)) {
    failures.push({
      ok: false, severity: 'block', code: 'locus-unsupported',
      reason: `Value ${value} for ${schema.label} is not supported by an explicit fiber construction distance in the source text.`,
    });
  }

  return { ok: failures.length === 0, failures };
}

/**
 * Validate a single extractor claim against `FIELD_SCHEMAS`. Pure.
 *
 * Behaviour:
 *   - `sedField` not in the registry ⇒ ok=true (we only validate what
 *     we know about; unknown paths are dropped by the extractor's
 *     `ALLOWED_SED_FIELDS` allow-list at the earlier boundary).
 *   - Wrong type ⇒ block + `type-mismatch`.
 *   - Numeric out-of-range ⇒ block + `out-of-range`.
 *   - String not in enum (when `enumValues` set, except `regulatory.state`
 *     which delegates to `normaliseStateToCode`) ⇒ block + `enum-mismatch`.
 *   - Missing unit token in source span ⇒ block + `missing-unit-token`.
 *   - Cross-field rule block ⇒ block + the rule's code.
 *   - Cross-field rule warn ⇒ ok=true + add a quality warning.
 *
 * Why surface ALL failures rather than first-fail? The admin rejection
 * panel (SCE-5) buckets by failure mode for "extractor improvement
 * opportunities" telemetry; first-fail would under-count compound
 * failures.
 */
export function validateClaim(input: SceValidateClaimInput): SceValidateClaimResult {
  const failures: Array<Extract<SceRuleResult, { ok: false }>> = [];
  const qualityWarnings: SceQualityWarning[] = [];

  if (!input.sedField) {
    // Untyped claim — no schema applies. The cross-checker can't
    // contradict the SED on a claim with no sedField, so this is safe.
    return { ok: true, failures, qualityWarnings };
  }
  const schema = FIELD_SCHEMAS[input.sedField];
  if (!schema) {
    // Path not in the registry. Not fatal — the extractor's allow-list
    // is the boundary that decides "is this a recognised SED path?";
    // SCE only deepens that for the paths we explicitly schema'd.
    return { ok: true, failures, qualityWarnings };
  }

  // Null / undefined values carry no schema violation — bail before the
  // context-free checks (preserves the pre-EX-1 early-return).
  if (input.value === undefined || input.value === null) {
    return { ok: true, failures, qualityWarnings };
  }

  // Steps 1-4 (type / range / enum / unit-token) — delegate to the shared
  // EX-1 primitive so the SED-path layer and the DOC channel run the
  // SAME context-free checks. `sourceSpan: input.sourceSpan ?? ''` is
  // passed unconditionally so the unit-token guard ALWAYS runs for a
  // SED claim (an empty span still fails the unit check, matching the
  // pre-refactor behaviour). The codes + reasons emitted by
  // `validateValue` are byte-identical to the inline checks this
  // replaced, so every existing consumer + test stays green.
  const contextFree = validateValue(input.sedField, input.value, {
    sourceSpan: input.sourceSpan ?? '',
  });
  for (const f of contextFree.failures) failures.push(f);

  // 5. State locus (special-cased — needs the source span).
  if (schema.path === 'regulatory.state') {
    const r = ruleStateLocus(
      {
        value: input.value,
        sed: input.sed,
        siteStateCode: input.siteStateCode,
        siteName: input.siteName,
      },
      input.sourceSpan,
    );
    if (!r.ok) {
      if (r.severity === 'block') failures.push(r);
      else qualityWarnings.push('sce-cross-field');
    }
  }

  // 6. Generic cross-field rules.
  if (schema.crossFieldRules) {
    for (const rule of schema.crossFieldRules) {
      const r = rule({
        value: input.value,
        sed: input.sed,
        siteStateCode: input.siteStateCode,
        siteName: input.siteName,
      });
      if (!r.ok) {
        if (r.severity === 'block') failures.push(r);
        else qualityWarnings.push('sce-cross-field');
      }
    }
  }

  const blocking = failures.some(f => f.severity === 'block');
  return { ok: !blocking, failures, qualityWarnings };
}

/* ── Helpers exported for tests ───────────────────────────────────── */

function truncate(s: string, n: number): string {
  if (!s || s.length <= n) return s ?? '';
  return s.slice(0, n).trimEnd() + '…';
}

/**
 * Telemetry summary — used by SCE-5's admin "Extractor rejections" rollup.
 * Buckets a flat list of `SceRejectionCode`s into a `code → count` map.
 */
export function summariseRejectionCodes(
  codes: readonly SceRejectionCode[],
): Readonly<Record<SceRejectionCode, number>> {
  const out: Partial<Record<SceRejectionCode, number>> = {};
  for (const c of codes) {
    out[c] = (out[c] ?? 0) + 1;
  }
  return out as Record<SceRejectionCode, number>;
}

/** Test-only: list of registered paths. Keeps the suite stable across additions. */
export function listSchemaPaths(): readonly string[] {
  return Object.keys(FIELD_SCHEMAS);
}
